import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import {
  ALL_MODEL_OPTIONS,
  GPT_THINKING_OPTIONS,
  PriceListModal,
  THINKING_OPTIONS,
  VERBOSITY_OPTIONS,
} from "../../Faq/components/ChatSettingsControls";
import MyUsageModal from "../../../../components/usageBilling/MyUsageModal";
import useMyUsage from "../../../../components/usageBilling/useMyUsage";
import {
  formatDay,
  formatUsd,
  percentUsed,
  resetDay,
} from "../../../../components/usageBilling/usageFormat";

const SECTION_LABEL =
  "text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const USAGE_BUTTON =
  "h-10 flex-1 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-orange-700 hover:bg-orange-50 dark:border-gray-700 dark:bg-gray-900 dark:text-orange-300 dark:hover:bg-gray-800";

function selectedModel(model) {
  return (
    ALL_MODEL_OPTIONS.find((o) => o.id === model) ??
    ALL_MODEL_OPTIONS.find((o) => o.isDefault) ??
    ALL_MODEL_OPTIONS[0]
  );
}

// The thinking tiers a model offers: GPT has its own two, and a model without a
// pro mode never shows "deep" (the same rule as the FAQ picker).
function thinkingOptionsFor(option) {
  return option?.provider === "gpt"
    ? GPT_THINKING_OPTIONS.filter((o) => !(o.id === "deep" && option.noDeepTier))
    : THINKING_OPTIONS;
}

// What the toolbar's settings button reads, "flash-lite · Balanced", with the
// full model id for its tooltip.
export function settingsSummary(model, thinking, language) {
  const option = selectedModel(model);
  const tiers = thinkingOptionsFor(option);
  const tier = tiers.find((o) => o.id === thinking) ?? tiers[0];
  return {
    model: option.label,
    short: option.label.replace(/^gemini-3\.5-|^gemini-|^gpt-5\.6-/, ""),
    thinking: tier.label[language],
  };
}

// The agent's answer settings and the user's AI usage. On wide screens it sits on
// the right of the page, in the space the document viewer uses - the page shows
// one or the other - and on phones it is a bottom sheet. The page opens it, and
// closes it on send and when a document opens.
export default function AssistantSettingsPanel({
  language,
  model,
  setModel,
  thinking,
  setThinking,
  verbosity,
  setVerbosity,
  onClose,
}) {
  const zh = language === "zh";
  const [usageOpen, setUsageOpen] = useState(false);
  const [pricesOpen, setPricesOpen] = useState(false);
  const option = selectedModel(model);
  const tiers = thinkingOptionsFor(option);
  const tier = tiers.find((o) => o.id === thinking) ?? tiers[0];
  const length =
    VERBOSITY_OPTIONS.find((o) => o.id === verbosity) ?? VERBOSITY_OPTIONS[1];

  // Escape closes the panel, unless one of its own windows is open on top.
  useEffect(() => {
    if (usageOpen || pricesOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, usageOpen, pricesOpen]);

  const pickModel = (next) => {
    setModel(next.id);
    // Keep thinking valid for the new model: Gemini's "fast" has no GPT tier, and
    // "deep" is missing on a model without a pro mode.
    if (!thinkingOptionsFor(next).some((o) => o.id === thinking)) {
      setThinking("balanced");
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={zh ? "關閉代理設定" : "Close agent settings"}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 md:hidden"
      />
      {/* Phones: a sheet sized in dvh, which leaves out the area behind the
          browser's toolbars. Wide screens: one screen tall and pinned while the
          page scrolls - the start screen can be taller than the window, and a
          panel following its height would run past the bottom edge. */}
      <aside
        id="assistant-settings"
        aria-label={zh ? "代理設定" : "Agent settings"}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-2xl bg-white shadow-2xl dark:bg-gray-900 md:sticky md:top-0 md:bottom-auto md:z-auto md:h-screen md:max-h-none md:w-[360px] md:shrink-0 md:self-start md:rounded-none md:border-l md:border-orange-500/30 md:shadow-none"
      >
        <div
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600 md:hidden"
        />
        <div className="flex items-center justify-between py-2 pl-4 pr-2 md:py-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {zh ? "代理設定" : "Agent settings"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={zh ? "關閉" : "Close"}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <FiX className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-4">
          <section className="space-y-1">
            <h3 className={SECTION_LABEL}>{zh ? "模型" : "Model"}</h3>
            {ALL_MODEL_OPTIONS.map((o) => {
              const selected = o.id === option.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => pickModel(o)}
                  className={`w-full rounded-xl border-2 px-3 py-2 text-left transition-colors ${
                    selected
                      ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10"
                      : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {o.label}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {o.speed[language]}
                    </span>
                    {o.isDefault && (
                      <span className="ml-auto rounded-full bg-gray-100 px-1.5 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        {zh ? "預設" : "Default"}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-gray-600 dark:text-gray-400">
                    {o.depth[language]}
                  </span>
                </button>
              );
            })}
          </section>

          <section className="space-y-1.5">
            <h3 className={SECTION_LABEL}>{zh ? "思考深度" : "Thinking"}</h3>
            <Segmented
              label={zh ? "思考深度" : "Thinking"}
              options={tiers}
              value={tier.id}
              onChange={setThinking}
              language={language}
            />
          </section>

          <section className="space-y-1.5">
            <h3 className={SECTION_LABEL}>{zh ? "回答長度" : "Answer length"}</h3>
            <Segmented
              label={zh ? "回答長度" : "Answer length"}
              options={VERBOSITY_OPTIONS}
              value={length.id}
              onChange={setVerbosity}
              language={language}
            />
          </section>
        </div>

        {/* Pinned to the bottom of the panel: the settings scroll above it. */}
        <div className="shrink-0 border-t border-gray-200 px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] dark:border-gray-700">
          <UsageSummary
            language={language}
            onDetails={() => setUsageOpen(true)}
            onPrices={() => setPricesOpen(true)}
          />
        </div>
      </aside>

      {usageOpen && (
        <MyUsageModal onClose={() => setUsageOpen(false)} language={language} />
      )}
      {pricesOpen && (
        <PriceListModal
          language={language}
          onClose={() => setPricesOpen(false)}
          priceNote={
            zh
              ? "費用與延遲為 FAQ 服務實測值，此頁尚未實測，僅供相對比較參考。"
              : "Measured against the FAQ service, not this one — use for relative comparison only."
          }
          modelOptions={ALL_MODEL_OPTIONS}
          showBillingRates
        />
      )}
    </>
  );
}

function Segmented({ label, options, value, onChange, language }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex gap-0.5 rounded-xl bg-gray-100 p-1 dark:bg-gray-800"
    >
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={o.id === value}
          onClick={() => onChange(o.id)}
          className={`h-9 flex-1 rounded-lg text-sm transition-colors ${
            o.id === value
              ? "bg-white font-semibold text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
              : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          }`}
        >
          {o.label[language]}
        </button>
      ))}
    </div>
  );
}

// This week's spend against the quota, loaded each time the panel opens; the
// full picture is one click away in "My AI usage".
function UsageSummary({ language, onDetails, onPrices }) {
  const zh = language === "zh";
  const { usage, error, loading, reload } = useMyUsage(1);
  const quota = usage?.this_week_quota_usd ?? null;
  const percent = usage ? percentUsed(usage.used_usd, quota) : null;
  const resets = usage ? formatDay(resetDay(usage.week_end), language) : "";

  return (
    <section
      aria-label={zh ? "AI 用量" : "AI usage"}
      className="space-y-2 rounded-2xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60"
    >
      <h3 className={SECTION_LABEL}>{zh ? "本週 AI 用量" : "AI usage this week"}</h3>
      {!usage && loading && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {zh ? "正在載入用量..." : "Loading usage..."}
        </p>
      )}
      {!usage && error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {zh ? "無法載入用量。" : "Couldn't load usage."}{" "}
          <button type="button" onClick={reload} className="font-medium underline">
            {zh ? "重試" : "Retry"}
          </button>
        </p>
      )}
      {usage && (
        <>
          <p className="text-gray-900 dark:text-white">
            <span className="text-lg font-bold">{formatUsd(usage.used_usd)}</span>
            {quota !== null && (
              <span className="ml-1.5 text-sm text-gray-600 dark:text-gray-400">
                {zh ? `／ ${formatUsd(quota)}` : `of ${formatUsd(quota)}`}
              </span>
            )}
          </p>
          {quota === null ? (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {zh ? "沒有適用的額度" : "No limit applies to you"}
            </p>
          ) : (
            <div
              role="meter"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(percent, 100)}
              aria-label={zh ? "本週額度使用率" : "This week's quota used"}
              className={`h-2 overflow-hidden rounded-full ${
                usage.over_quota
                  ? "bg-red-100 dark:bg-red-900/30"
                  : "bg-orange-100 dark:bg-orange-900/30"
              }`}
            >
              <div
                className={`h-full rounded-full ${usage.over_quota ? "bg-red-500" : "bg-orange-500"}`}
                style={{ width: `${Math.min(percent, 100)}%` }}
              />
            </div>
          )}
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {usage.over_quota && usage.enforced
              ? zh
                ? `已用完本週額度，${resets}恢復使用。`
                : `This week's limit is reached. Available again on ${resets}.`
              : zh
                ? `${resets}重置`
                : `Resets ${resets}`}
          </p>
        </>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={onDetails} className={USAGE_BUTTON}>
          {zh ? "用量明細" : "Usage details"}
        </button>
        <button type="button" onClick={onPrices} className={USAGE_BUTTON}>
          {zh ? "價目表" : "Price list"}
        </button>
      </div>
    </section>
  );
}
